import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Coins, Receipt, TrendingUp, AlertTriangle, ArrowRight, Plus, Sparkles, BellRing, CheckCircle2, FileSignature, ChevronRight } from "lucide-react";
import { get } from "@/lib/api";
import { fmtDate, pct, num } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useAssistantOptional } from "@/context/AssistantContext";
import { Button } from "@/components/ui/button";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatStrip } from "@/components/dashboard/StatStrip";
import { Money, Panel, QuietLink, StackBar, Bar, FinanceBrief, HealthBadge, Provenance } from "@/components/finance/FinanceBits";
import { CashflowChart, RevenueChart } from "@/components/finance/Charts";
import { compactKr } from "@/components/finance/money";

const KIND_ICON = { faktura: BellRing, godkjenning: CheckCircle2, kontrakt: FileSignature, kostnad: Receipt };
const KIND_ACTION = { faktura: "Åpne faktura", godkjenning: "Godkjenn", kontrakt: "Avklar", kostnad: "Avklar" };

/**
 * Økonomi-oversikt – the financial control centre: brief, four numbers, cashflow as the dominant visual with the
 * action feed beside it, then receivables, profitability, billing queue and revenue outlook.
 */
export default function FinanceOverviewPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const assistant = useAssistantOptional();
  const q = useQuery({ queryKey: ["finance", "overview"], queryFn: () => get("/finance/overview"), refetchInterval: 60000 });
  const d = q.data;

  const stats = useMemo(() => {
    if (!d) return [];
    const r = d.receivables;
    const inv = d.invoicing;
    const c = d.contracts;
    const p = d.portfolio;
    return [
      { id: "outstanding", icon: Coins, tone: r.overdue > 0 ? "danger" : undefined, label: "Utestående", value: compactKr(r.total), sub: r.overdue > 0 ? `${compactKr(r.overdue)} forfalt · ${r.count} åpne fakturaer` : `${r.count} åpne fakturaer · ingenting forfalt`, onClick: () => navigate("/okonomi/fakturaer?status=open") },
      { id: "invoiced", icon: Receipt, label: "Fakturert denne måneden", value: compactKr(inv.this_month.net), sub: inv.trend_pct != null ? `${inv.trend_pct > 0 ? "+" : ""}${inv.trend_pct} % mot forrige · ${inv.this_month.count} fakturaer` : `${inv.this_month.count} fakturaer · eks. mva`, onClick: () => navigate("/okonomi/fakturaer") },
      { id: "mrr", icon: TrendingUp, tone: "primary", label: "Kontraktsfestet månedsleie", value: compactKr(c.mrr), sub: `${c.active} aktive kontrakter · ${compactKr(c.backlog)} gjenstår`, onClick: () => navigate("/okonomi/kontrakter?status=aktiv") },
      { id: "margin", icon: AlertTriangle, tone: p.risk ? "warning" : undefined, label: "Dekningsgrad portefølje", value: p.forecast_margin_pct != null ? pct(p.forecast_margin_pct, 1) : "–", sub: p.risk ? `${p.risk} prosjekt i risiko · prognose` : `${p.active_projects} aktive prosjekter · prognose`, onClick: () => navigate("/okonomi/prosjekter") },
    ];
  }, [d, navigate]);

  if (q.isLoading) return <LoadingState className="py-32" />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;

  const cf = d.cashflow;
  const aging = d.receivables;
  const ranking = d.portfolio.ranking || [];
  const attention = d.attention || [];
  const ready = d.invoicing.ready_to_bill || [];
  const expiring = d.contracts.expiring_90d || [];
  const revenue = d.forecast?.months || [];
  const maxRev = Math.max(1, ...ranking.map((r) => r.forecast_revenue || 0));

  return (
    <div data-testid="finance-overview-page">
      <header className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between" data-testid="finance-hero">
        <div className="min-w-0 max-w-3xl">
          <p className="text-[12.5px] leading-4 text-muted-foreground" data-testid="finance-date">
            <span className="first-letter:uppercase">{fmtDate(d.today, "EEEE d. MMMM yyyy")}</span>
            <span className="mx-1.5 text-muted-foreground/60">·</span>
            Internt økonomiregister · NOK · beløp eks. mva der ikke annet er sagt
          </p>
          <h1 className="mt-1.5 text-[26px] font-semibold leading-8 tracking-tight sm:text-[30px] sm:leading-9">Økonomi</h1>
          <FinanceBrief sentences={d.brief} className="mt-3" />
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {assistant && (
            <Button variant="outline" onClick={() => assistant.ask("Gi meg en økonomisk statusrapport: utestående, forfalt, fakturering, kontantstrøm og prosjekter i risiko – hva bør jeg gjøre i dag?")} data-testid="finance-ask-ai-button">
              <Sparkles /> Spør assistenten
            </Button>
          )}
          {can("finance:write") && (
            <Button onClick={() => navigate("/okonomi/fakturaer?ny=1")} data-testid="finance-new-invoice-button">
              <Plus /> Ny faktura
            </Button>
          )}
        </div>
      </header>

      <StatStrip items={stats} className="mt-8" testId="finance-stats" idPrefix="finance-stat" />

      {/* Cashflow + action feed */}
      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-8">
        <Panel
          className="lg:col-span-7 xl:col-span-8"
          title="Kontantstrøm neste 13 uker"
          meta={`Fra ${compactKr(cf.opening_balance)} i dag til ${compactKr(cf.closing_balance)} · laveste punkt ${compactKr(cf.min_balance)} i ${cf.min_week}`}
          action={<QuietLink to="/okonomi/prognose" testId="finance-cashflow-link">Prognose</QuietLink>}
          testId="finance-cashflow"
        >
          <CashflowChart weeks={cf.weeks} height={270} />
          <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-[12px] text-muted-foreground">
            <Provenance kind="prognose" />
            <span>Innbetalinger antatt på forfall (forfalte innen 7 dager)</span>
            <span>Planlagt fakturering inkl. mva ved forfall</span>
            <span>Faste kostnader {compactKr(cf.assumptions?.fixed_costs_monthly)}/mnd</span>
          </div>
        </Panel>

        <Panel
          className="lg:col-span-5 xl:col-span-4"
          divided
          title="Krever handling"
          meta={attention.length ? `${attention.length} poster · forfalte fakturaer, godkjenninger, kontrakter` : "Ingenting venter akkurat nå"}
          action={<QuietLink to="/okonomi/fakturaer?status=forfalt" testId="finance-attention-link">Forfalte</QuietLink>}
          testId="finance-attention"
        >
          {attention.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="Alt er under kontroll" description="Ingen forfalte fakturaer, godkjenninger eller kontrakter som krever handling." className="py-8" testId="finance-attention-empty" />
          ) : (
            <ul className="divide-y divide-border/70" data-testid="finance-attention-list">
              {attention.slice(0, 8).map((a) => {
                const Icon = KIND_ICON[a.kind] || AlertTriangle;
                return (
                  <li key={`${a.kind}-${a.id}`}>
                    <button
                      type="button"
                      onClick={() => navigate(a.url)}
                      className="group -mx-2 flex w-[calc(100%+1rem)] items-start gap-3 rounded-lg px-2 py-2.5 text-left transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none"
                      data-testid={`finance-attention-item-${a.kind}`}
                    >
                      <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg", a.severity === "danger" ? "bg-rose-500/10 text-rose-600 dark:text-rose-400" : a.severity === "warning" ? "bg-amber-500/10 text-amber-700 dark:text-amber-300" : "bg-muted text-foreground/70")} aria-hidden>
                        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium leading-5">{a.title}</span>
                        <span className="block truncate text-[12px] leading-4 text-muted-foreground">{a.meta}</span>
                      </span>
                      <span className="mt-1 inline-flex shrink-0 items-center gap-0.5 text-[11.5px] text-muted-foreground opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
                        {KIND_ACTION[a.kind] || "Åpne"} <ChevronRight className="h-3 w-3" />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>

      {/* Receivables · profitability · billing queue */}
      <div className="mt-12 grid gap-10 lg:grid-cols-3 lg:gap-8">
        <Panel title="Utestående etter alder" meta={`${compactKr(aging.total)} totalt · ${pct(aging.overdue_pct, 1)} forfalt${aging.dso ? ` · DSO ${aging.dso} dager` : ""}`} action={<QuietLink to="/okonomi/fakturaer?status=open">Alle åpne</QuietLink>} testId="finance-aging">
          <StackBar
            height="h-2.5"
            segments={[
              { id: "ok", value: aging.buckets.ikke_forfalt, tone: "primary", label: "Ikke forfalt" },
              { id: "b1", value: aging.buckets["1_30"], tone: "warning", label: "1–30 d" },
              { id: "b2", value: aging.buckets["31_60"], tone: "danger", label: "31–60 d" },
              { id: "b3", value: (aging.buckets["61_90"] || 0) + (aging.buckets["90_plus"] || 0), tone: "ink", label: "61+ d" },
            ]}
            total={aging.total}
          />
          <dl className="mt-3 grid grid-cols-4 gap-2 text-[11.5px]">
            {[
              ["Ikke forfalt", aging.buckets.ikke_forfalt, "bg-primary"],
              ["1–30 d", aging.buckets["1_30"], "bg-amber-400"],
              ["31–60 d", aging.buckets["31_60"], "bg-rose-500"],
              ["61+ d", (aging.buckets["61_90"] || 0) + (aging.buckets["90_plus"] || 0), "bg-foreground/70"],
            ].map(([l, v, c]) => (
              <div key={l} className="min-w-0">
                <dt className="flex items-center gap-1.5 text-muted-foreground">
                  <span className={cn("h-1.5 w-1.5 rounded-full", c)} aria-hidden /> {l}
                </dt>
                <dd className="truncate font-medium tabular">{compactKr(v)}</dd>
              </div>
            ))}
          </dl>
          <ul className="mt-5 divide-y divide-border/70" data-testid="finance-aging-customers">
            {aging.top.filter((t) => t.total > 0).slice(0, 5).map((t) => (
              <li key={t.customer_id}>
                <button type="button" onClick={() => navigate(`/okonomi/fakturaer?customer_id=${t.customer_id}&status=open`)} className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-2 text-left text-[13px] transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none" data-testid="finance-aging-customer">
                  <span className="min-w-0 flex-1 truncate">{t.customer_name}</span>
                  {t.overdue > 0 && <span className="shrink-0 text-[11.5px] font-medium text-rose-600 dark:text-rose-400">{t.max_days} d</span>}
                  <Money v={t.total} compact className="w-20 shrink-0 text-right font-medium" />
                </button>
              </li>
            ))}
          </ul>
        </Panel>

        <Panel divided title="Prosjektlønnsomhet" meta={`${d.portfolio.active_projects} aktive · prognose ${compactKr(d.portfolio.forecast_revenue)} inntekt`} action={<QuietLink to="/okonomi/prosjekter">Alle prosjekter</QuietLink>} testId="finance-margins">
          <ul className="space-y-3.5" data-testid="finance-margin-list">
            {[...ranking].slice(0, 6).map((r) => (
              <li key={r.project_id}>
                <button type="button" onClick={() => navigate(`/okonomi/prosjekter/${r.project_id}`)} className="-mx-2 block w-[calc(100%+1rem)] rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none" data-testid="finance-margin-item">
                  <div className="flex items-baseline justify-between gap-3 text-[13px]">
                    <span className="min-w-0 truncate">{r.project_name.split(" – ")[0]}</span>
                    <span className={cn("shrink-0 font-medium tabular", r.health === "risiko" ? "text-rose-600 dark:text-rose-400" : r.health === "folg_opp" ? "text-amber-700 dark:text-amber-300" : "")}>{pct(r.forecast_margin_pct, 1)}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <Bar value={r.forecast_revenue} max={maxRev} tone={r.health === "risiko" ? "danger" : r.health === "folg_opp" ? "warning" : "primary"} className="flex-1" />
                    <span className="w-16 shrink-0 text-right text-[11px] text-muted-foreground tabular">{compactKr(r.forecast_revenue)}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          {ranking.some((r) => r.health !== "ok") && (
            <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
              <HealthBadge health="risiko" /> = prognose under budsjett · <Provenance kind="prognose" />
            </div>
          )}
        </Panel>

        <Panel divided title="Klar til fakturering" meta={ready.length ? `${ready.length} perioder · ${compactKr(d.invoicing.ready_to_bill_amount)} eks. mva` : "Alle løpende perioder er fakturert"} action={<QuietLink to="/okonomi/kontrakter?status=aktiv">Kontrakter</QuietLink>} testId="finance-ready">
          {ready.length === 0 ? (
            <EmptyState icon={CheckCircle2} title="Ingenting å fakturere" description="Neste periode blir klar rundt den 25." className="py-8" testId="finance-ready-empty" />
          ) : (
            <ul className="divide-y divide-border/70" data-testid="finance-ready-list">
              {ready.slice(0, 6).map((r) => (
                <li key={r.id}>
                  <button type="button" onClick={() => navigate(`/okonomi/kontrakter/${r.id}`)} className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none" data-testid="finance-ready-item">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px]">{r.customer_name}</span>
                      <span className="block truncate text-[11.5px] text-muted-foreground">
                        {r.contract_number} · {fmtDate(r.period_start, "d. MMM")} – {fmtDate(r.period_end, "d. MMM")}
                      </span>
                    </span>
                    <Money v={r.amount} compact className="shrink-0 font-medium" />
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {d.approvals.count > 0 && (
            <div className="mt-4 rounded-xl bg-amber-500/10 px-3 py-2.5 text-[12.5px] text-amber-900 dark:text-amber-200" data-testid="finance-approvals-callout">
              <span className="font-medium">{d.approvals.count} poster venter på godkjenning</span> · {compactKr(d.approvals.amount)} · terskel {compactKr(d.approval_threshold)}
            </div>
          )}
        </Panel>
      </div>

      {/* Revenue outlook + expiring contracts */}
      <div className="mt-12 grid gap-10 lg:grid-cols-12 lg:gap-8">
        <Panel className="lg:col-span-8" title="Inntekt – siste 6 og neste 12 måneder" meta={`Kontraktsfestet ${compactKr(d.forecast.summary.contracted_12m)} + vektet pipeline ${compactKr(d.forecast.summary.pipeline_weighted_12m)} neste 12 mnd`} action={<QuietLink to="/okonomi/prognose">Detaljer</QuietLink>} testId="finance-revenue">
          <RevenueChart months={revenue} height={230} />
        </Panel>
        <Panel className="lg:col-span-4" divided title="Kontrakter som utløper" meta={expiring.length ? `${expiring.length} innen 90 dager` : "Ingen utløper de neste 90 dagene"} action={<QuietLink to="/okonomi/kontrakter?expiring=90">Alle</QuietLink>} testId="finance-expiring">
          {expiring.length === 0 ? (
            <div className="text-[13px] text-muted-foreground">Backlog {compactKr(d.contracts.backlog)} fordelt på {d.contracts.active} aktive og {d.contracts.signed_upcoming} signerte kontrakter.</div>
          ) : (
            <ul className="divide-y divide-border/70" data-testid="finance-expiring-list">
              {expiring.map((e) => (
                <li key={e.id}>
                  <button type="button" onClick={() => navigate(`/okonomi/kontrakter/${e.id}`)} className="-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors duration-150 hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none" data-testid="finance-expiring-item">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px]">{e.title.replace("Leieavtale ", "")}</span>
                      <span className="block truncate text-[11.5px] text-muted-foreground">
                        {e.customer_name} · utløper {fmtDate(e.end_date, "d. MMM")} · {num(e.notice_period_days)} d oppsigelse
                      </span>
                    </span>
                    <span className="shrink-0 text-right text-[12.5px] tabular">
                      <Money v={e.monthly_value} compact className="font-medium" />
                      <span className="block text-[11px] text-muted-foreground">/mnd</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
